import { ColDef, GridReadyEvent, IServerSideDatasource, ServerSideRowSelectionState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ManagedSupplierGroupDealsApi } from "apis/ManagedSupplierGroupDealsApi";
import { Title, Modal } from "components";
import { ZButton } from "components/ZButton";
import { ZIconType } from "helpers/utility/iconMap";
import { ManagedSupplierGroupDealsTemplateView } from "models/dbModels/managedSupplierDeals/ManagedSupplierDealsTemplate";
import { useCallback, useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { DealTermModel, GetTermsDescription } from "./NewSupplierGroupDeal";
import { ManagedSupplier } from "models/dbModels/managedSuppliers/ManagedSupplier";
import { getSuppliers } from "redux/slices/Suppliers";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import * as datefns from "date-fns";
import { SearchState } from "components/AgGrid/FilterComponents/Search";
import { FilterState } from "components/AgGrid/FilterComponents/Filters";
import { AgGridToolbar } from "components/AgGrid/AgGridToolbar";
import { ZTooltip } from "components/ZTooltip";
import { RootState } from "redux/store";


export function SupplierGroupDeals() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const user = useAppSelector((state: RootState) => state.logedinUser);	
	const tableRef = useRef<AgGridReact>(null);
	const [showDelete, setShowDelete] = useState<boolean>(false);
	const [suppliers, setSuppliers] = useState<ManagedSupplier[]>([]);

	//for the server side selection, we need to track selectAll and the nodes that are toggled the opposite
	const [selectAll, setSelectAll] = useState<boolean>(false);
	const [toggledNodes, setToggledNodes] = useState<number[]>([]);
	const [selectedCount, setSelectedCount] = useState<number>(0);

	const [totalRows, setTotalRows] = useState<number>(0);
	const gridSearch = useRef<{ searchState?: SearchState; filterState?: FilterState }>({});

	useEffect(() => {}, []);

	useEffect(() => {
		dispatch(getSuppliers()).then((res) => {
			setSuppliers(res.data);
		});
	}, []);

	const getServerSideDatasource: () => IServerSideDatasource = () => {
		return {
			getRows: async (params) => {
				params.api.showLoadingOverlay();

				console.log("[Datasource] - rows requested by grid: ", params.request);

				const response = await ManagedSupplierGroupDealsApi.Search({
					keyword: gridSearch.current.searchState?.keyword || "",
					contains: gridSearch.current.searchState?.contains || false,
					offset: params.request.startRow,
					limit: params.request.endRow !== undefined && params.request.startRow !== undefined ? params.request.endRow - params.request.startRow : 100,
				});
				setTotalRows(response.data.totalRows);
				params.success({
					rowData: response.data.data,
					rowCount: response.data.totalRows,
				});

				if (response.data.data.length === 0) {
					params.api.showNoRowsOverlay();
				} else {
					params.api.hideOverlay();
				}
			},
		};
	};

	const onGridReady = useCallback((params: GridReadyEvent) => {
		var datasource = getServerSideDatasource();
		// register the datasource with the grid
		params.api!.setGridOption("serverSideDatasource", datasource);
	}, []);

	const columns: ColDef<ManagedSupplierGroupDealsTemplateView>[] = [
		{
			headerName: "Name",
			sortable: true,
			field: "name",
			minWidth: 110,
			flex: 1,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionCurrentPageOnly: false,
			cellRenderer: (params: any) => {
				return <Link to={`/app/supplier-assorted-deals/${params.data?.id}`}>{params.data?.name}</Link>;
			},
		},
		{
			headerName: "Supplier",
			sortable: true,
			field: "managedSupplierID",
			minWidth: 110,
			flex: 1,
			cellRenderer: (params: any) => {
				return <Link to={`/app/supplier-assorted-deals/${params.data?.id}`}>{suppliers.find(i => i.id === params.data?.managedSupplierID)?.name}</Link>;
			},
		},
		{
			headerName: "Deal Terms",
			sortable: true,
			field: "terms",
			minWidth: 110,
			flex: 2,
			cellRenderer: (params: any) => {
				const terms = JSON.parse(params.data.terms) as DealTermModel[];
				return  <Link to={`/app/supplier-assorted-deals/${params.data?.id}`}>{params.data && params.data.terms ? GetTermsDescription(terms, params.data.minOrderType) : ""}</Link>;
			},
		},
		// {
		// 	headerName: "Min Order",
		// 	sortable: true,
		// 	field: "minOrder",
		// 	minWidth: 110,
		// 	flex: 1,
		// 	cellRenderer: (params: any) => {
		// 		return <Link to={`/app/supplier-assorted-deals/${params.data?.id}`}>{params.data?.minOrder}</Link>;
		// 	},
		// },
		{
			headerName: "Term Type (Units vs Dollars)",
			sortable: true,
			field: "minOrderType",
			minWidth: 110,
			flex: 1,
			cellRenderer: (params: any) => {
				return <Link to={`/app/supplier-assorted-deals/${params.data?.id}`}>{params.data?.minOrderType === "C" ? " Dollars" : " Units"}</Link>;
			},
		},
		{
			headerName: "No. Of Lines",
			field: "stockCount",
			sortable: true,
			flex: 1,
		},
		{
			headerName: "No. Of Stores",
			field: "storeCount",
			sortable: true,
			flex: 1,
		},
		{
			headerName: "Deal Notes",
			field: "notes",
			sortable: true,
			flex: 1,
		},
		{
			headerName: "Start Date",
			field: "startDate",
			sortable: true,
			flex: 1,
			valueGetter: (params) => {
				return params.data?.startDate ? datefns.format(new Date(params.data?.startDate), "dd/MM/yyyy") : "";
			},
		},
		{
			headerName: "End Date",
			field: "endDate",
			sortable: true,
			flex: 1,
			valueGetter: (params) => {
				return params.data?.endDate ? datefns.format(new Date(params.data?.endDate), "dd/MM/yyyy") : "";
			},
		},
		{
			headerName: "Last Modified",
			field: "lastModified",
			sortable: true,
			flex: 1,
			valueGetter: (params) => {
				return params.data?.lastModified ? datefns.format(new Date(params.data.lastModified), "dd/MM/yyyy hh:mm:ss aa") : "";
			},
		},
	];

	useEffect(() => {
		if (selectAll) {
			setSelectedCount((tableRef.current?.api.paginationGetRowCount() || 0) - toggledNodes.length);
		} else {
			setSelectedCount(toggledNodes.length);
		}
	}, [selectAll, toggledNodes]);

	return (
		<>
			<Title pageTitle="Assorted Deals" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<AgGridToolbar 
									search={true}
									className="mb-1 px-1"
									handleSubmit={(searchState, filterState) => {
										gridSearch.current = { searchState, filterState };
										tableRef.current?.api.refreshServerSide();
									}}
									searchProps={
										{totalRows: totalRows, placeholder: "by Name", hideContains: false}
									}
									children={(<div className="d-inline-flex gap-1">
	
	
										<ZTooltip enabled={user.permissions.supplierGroupDeals.canCreate} text={user.permissions.supplierGroupDeals.canCreate ? "Add" : "You do not have permission to add"}>
											<ZButton icon={ZIconType.Add} variant="light" buttonSize="md" onClick={() => navigate("/app/supplier-assorted-deals/new")} 
												className={!user.permissions.supplierGroupDeals.canCreate ? "invisible" : ""}/>
										</ZTooltip>
	
										<ZTooltip enabled={user.permissions.supplierGroupDeals.canDelete} text={user.permissions.supplierGroupDeals.canDelete ? "Delete" : "You do not have permission to delete"}>
											<ZButton
												buttonSize="md"
												icon={ZIconType.Delete}
												variant="light"
												onClick={() => setShowDelete(true)}
												disabled={selectedCount === 0}
												className={!user.permissions.supplierGroupDeals.canDelete ? "invisible" : ""}
											/>
										</ZTooltip>
									</div>
										
									)}
								/> 
							<Row>
								<div className="page-container">
									<AgGridReact
										ref={tableRef}
										columnDefs={columns}
										rowModelType="serverSide"
										pagination={true}
										paginationPageSize={20}
										cacheBlockSize={100}
										onGridReady={onGridReady}
										rowSelection="multiple"
										className={`ag-theme-quartz ag-theme-custom w-100 flex-grow`}
										containerStyle={{ height: "75dvh" }}
										suppressRowClickSelection
										singleClickEdit
										rowMultiSelectWithClick
										groupAllowUnbalanced
										animateRows
										suppressContextMenu
										editType="fullRow"
										stopEditingWhenCellsLoseFocus={true}
										onSelectionChanged={(e) => {
											const serverSide = e.api.getServerSideSelectionState() as ServerSideRowSelectionState;
											if (serverSide != null) {
												setSelectAll(serverSide.selectAll);
												setToggledNodes(serverSide.toggledNodes.map(i => e.api.getRowNode(i)!.data.id));
											}
										}}
									/>
								</div>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>


			
			<Modal
				title="Confirm Delete"
				show={showDelete}
				buttonType="primary"
				buttonText={"Continue"}
				buttonCloseText="Cancel"
				handleSubmit={() => {
					if (selectAll || toggledNodes.length > 0) {
						ManagedSupplierGroupDealsApi.Delete({  ids: (selectAll ? [] : toggledNodes), excludedIds: (selectAll ? toggledNodes : []) }).then((response) => {
							if (response.data.data) {
								tableRef.current?.api.refreshServerSide();
							}
						});
					}

					setShowDelete(false);
					tableRef.current?.api.deselectAll();
					tableRef.current?.api.refreshServerSide();
				}}
				handleClose={() => setShowDelete(false)}
				description={
					<>
						<p>You are about to delete {selectedCount} selected {selectedCount > 1 ? "items": "item" }.</p>
						<p>Are you sure you want to delete?</p>
					</>
				}
			/>
		</>
	);
}
